/**
 * DeductionFields — the two rendering surfaces for non-statutory deductions:
 * the read-only itemisation both previews show, and the row editor the record
 * and correction gates embed.
 *
 * Split from deductions.ts only because react-refresh requires a module to
 * export components OR values, not both. The reasoning about kinds, the
 * tri-state and the give_as_you_earn refusal lives in that file's header.
 */
import { Plus, Trash2 } from 'lucide-react'
import type { PayrollDeduction } from '../../types'
import { money } from '../../../../lib/money'
import {
  DEDUCTION_KINDS, FIXED_OR_VARIABLE, FIXED_OR_VARIABLE_LABEL, KIND_LABEL, emptyDraft, kindLabel,
  type DeductionDraft,
} from './deductions'

/**
 * The read-only itemisation — the s.8(2)(b) particulars.
 *
 * An EMPTY list is rendered as a sentence, not as blank space and not as an em
 * dash: "no deductions" is a real answer, and the surrounding total already
 * says £0.00. See PayrollLedgerRow.deductions for why empty is a zero rather
 * than an absence.
 */
export function DeductionList({ deductions, emptyText = 'No deductions.' }: {
  deductions: PayrollDeduction[]
  emptyText?: string
}) {
  if (deductions.length === 0) {
    return <div className="text-13 text-ink-secondary leading-relaxed">{emptyText}</div>
  }
  return (
    <ul className="flex flex-col gap-1">
      {deductions.map((d, i) => (
        <li key={`${d.kind}-${d.purpose}-${i}`} className="text-13 text-ink-body leading-relaxed">
          <span className="tabular-nums font-medium text-ink-primary">{money(d.amountPence / 100)}</span>
          {' — '}
          <span>{d.purpose}</span>
          <span className="text-ink-muted">
            {' ('}{kindLabel(d.kind)}, {FIXED_OR_VARIABLE_LABEL[d.fixedOrVariable] ?? d.fixedOrVariable}{')'}
          </span>
        </li>
      ))}
    </ul>
  )
}

const rowInputCls = 'h-9 px-2 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'

/**
 * The row editor. `onChange` receives the WHOLE list on every mutation so the
 * caller can route it through its own invalidate-on-edit wrapper — a deduction
 * row is an input like any other, and editing one must discard a standing
 * preview exactly as editing gross does.
 */
export function DeductionRowsEditor({ idPrefix, drafts, onChange, disabled = false }: {
  idPrefix: string
  drafts: DeductionDraft[]
  onChange: (next: DeductionDraft[]) => void
  disabled?: boolean
}) {
  function patch(i: number, field: keyof DeductionDraft, value: string) {
    onChange(drafts.map((d, j) => (j === i ? { ...d, [field]: value } : d)))
  }

  return (
    <div className="flex flex-col gap-2">
      {drafts.length > 0 && (
        <div className="flex flex-col gap-2">
          {drafts.map((d, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_7rem_7rem_2rem] gap-2 items-end">
              <div>
                {i === 0 && <label htmlFor={`${idPrefix}-purpose-0`} className="block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">Purpose</label>}
                <input id={`${idPrefix}-purpose-${i}`} value={d.purpose} disabled={disabled}
                  aria-label={`Deduction ${i + 1} purpose`}
                  onChange={e => patch(i, 'purpose', e.target.value)}
                  placeholder="Union subscription" className={`${rowInputCls} w-full`} />
              </div>
              <div>
                {i === 0 && <label htmlFor={`${idPrefix}-kind-0`} className="block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">Kind</label>}
                <select id={`${idPrefix}-kind-${i}`} value={d.kind} disabled={disabled}
                  aria-label={`Deduction ${i + 1} kind`}
                  onChange={e => patch(i, 'kind', e.target.value)} className={`${rowInputCls} w-full`}>
                  {DEDUCTION_KINDS.map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
              </div>
              <div>
                {i === 0 && <label htmlFor={`${idPrefix}-amount-0`} className="block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">Amount (£)</label>}
                <input id={`${idPrefix}-amount-${i}`} type="number" min={0} step={0.01} value={d.amount} disabled={disabled}
                  aria-label={`Deduction ${i + 1} amount`}
                  onChange={e => patch(i, 'amount', e.target.value)}
                  placeholder="15.00" className={`${rowInputCls} w-full text-right tabular-nums`} />
              </div>
              <div>
                {i === 0 && <label htmlFor={`${idPrefix}-fov-0`} className="block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">Fixed/variable</label>}
                <select id={`${idPrefix}-fov-${i}`} value={d.fixedOrVariable} disabled={disabled}
                  aria-label={`Deduction ${i + 1} fixed or variable`}
                  onChange={e => patch(i, 'fixedOrVariable', e.target.value)} className={`${rowInputCls} w-full`}>
                  {FIXED_OR_VARIABLE.map(v => <option key={v} value={v}>{FIXED_OR_VARIABLE_LABEL[v]}</option>)}
                </select>
              </div>
              <button type="button" disabled={disabled}
                onClick={() => onChange(drafts.filter((_, j) => j !== i))}
                aria-label={`Remove deduction ${i + 1}`}
                className="h-9 w-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors disabled:opacity-40">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div>
        <button type="button" disabled={disabled}
          onClick={() => onChange([...drafts, emptyDraft()])}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-11 font-semibold rounded-control border border-edge-control bg-surface-control text-ink-body transition-colors hover:bg-surface-nav-hover disabled:opacity-40">
          <Plus size={12} /> Add a deduction
        </button>
      </div>
    </div>
  )
}
